import { useState } from "react";
import { PageHeader } from "@/components/base/ui";
import { sendQuoteEmail, isEmailJSConfigured, getEmailTo, getConfigError } from "@/config/emailjs";

export default function Quote() {
  const [formState, setFormState] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const configError = getConfigError();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormState("submitting");
    setErrorMessage("");

    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);

    const params: Record<string, unknown> = {
      to_email: getEmailTo(),
      company_name: formData.get("company_name"),
      country: formData.get("country"),
      contact_name: formData.get("contact_name"),
      email: formData.get("email"),
      stone_type: formData.get("stone_type"),
      quantity: formData.get("quantity"),
      budget: formData.get("budget") || "Not specified",
      application: formData.get("application") || "Not specified",
      project_details: formData.get("project_details") || "Not provided",
      page: "Quote Request",
    };

    try {
      if (!isEmailJSConfigured()) {
        throw new Error(configError || "EmailJS is not configured.");
      }
      await sendQuoteEmail(params);
      setFormState("success");
      form.reset();
    } catch (err) {
      setFormState("error");
      setErrorMessage(err instanceof Error ? err.message : "Something went wrong.");
    }
  };

  return (
    <div>
      <PageHeader
        title="Request a Quotation"
        subtitle="Tell us about your project and we'll provide a detailed, competitive quotation within 24 hours."
      />

      <section className="py-16 md:py-24 bg-stone-50">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-lg p-6 md:p-10 border border-stone-200">
              <h2 className="font-serif text-2xl md:text-3xl font-semibold text-stone-900 mb-2">
                B2B Quotation Request
              </h2>
              <p className="text-stone-500 text-sm font-sans mb-8">
                Fill in the details below and our team will prepare a customized quotation for your project.
              </p>

              {formState === "success" && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <i className="ri-check-line text-green-500 text-lg"></i>
                    <p className="text-green-700 text-sm font-sans">
                      Thank you! Your quotation request has been received. Our team will contact you within 24 hours with a detailed proposal.
                    </p>
                  </div>
                </div>
              )}

              {formState === "error" && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <i className="ri-error-warning-line text-red-500 text-lg"></i>
                    <p className="text-red-700 text-sm font-sans">
                      {errorMessage || "Something went wrong. Please try again or contact us directly at contact@lithos.pro."}
                    </p>
                  </div>
                </div>
              )}

              {configError && (
                <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <i className="ri-settings-3-line text-amber-500 text-lg mt-0.5"></i>
                    <div>
                      <p className="text-amber-700 text-sm font-sans font-medium mb-1">
                        Email not configured yet
                      </p>
                      <p className="text-amber-600 text-xs font-sans">
                        {configError} Add them to your .env file or use the form below and we will contact you via WhatsApp or phone.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <form
                id="quote-form"
                onSubmit={handleSubmit}
                className="flex flex-col gap-5"
              >
                {/* Company Info */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-stone-700 text-sm font-sans font-medium mb-2">
                      Company Name *
                    </label>
                    <input
                      type="text"
                      name="company_name"
                      required
                      className="w-full px-4 py-3 border border-stone-200 rounded-md text-sm font-sans text-stone-700 focus:outline-none focus:border-gold-500 transition-colors"
                      placeholder="Your company name"
                    />
                  </div>
                  <div>
                    <label className="block text-stone-700 text-sm font-sans font-medium mb-2">
                      Country *
                    </label>
                    <input
                      type="text"
                      name="country"
                      required
                      className="w-full px-4 py-3 border border-stone-200 rounded-md text-sm font-sans text-stone-700 focus:outline-none focus:border-gold-500 transition-colors"
                      placeholder="e.g. France, Belgium, Spain"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-stone-700 text-sm font-sans font-medium mb-2">
                      Contact Name *
                    </label>
                    <input
                      type="text"
                      name="contact_name"
                      required
                      className="w-full px-4 py-3 border border-stone-200 rounded-md text-sm font-sans text-stone-700 focus:outline-none focus:border-gold-500 transition-colors"
                      placeholder="Full name"
                    />
                  </div>
                  <div>
                    <label className="block text-stone-700 text-sm font-sans font-medium mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      className="w-full px-4 py-3 border border-stone-200 rounded-md text-sm font-sans text-stone-700 focus:outline-none focus:border-gold-500 transition-colors"
                      placeholder="your@company.com"
                    />
                  </div>
                </div>

                {/* Stone Details */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-stone-700 text-sm font-sans font-medium mb-2">
                      Type of Stone *
                    </label>
                    <select
                      name="stone_type"
                      required
                      className="w-full px-4 py-3 border border-stone-200 rounded-md text-sm font-sans text-stone-700 focus:outline-none focus:border-gold-500 transition-colors bg-white"
                    >
                      <option value="">Select stone type</option>
                      <option value="Blanc Imperial Marble">Blanc Imperial Marble</option>
                      <option value="Gris Perle Marble">Gris Perle Marble</option>
                      <option value="Vert Emeraude Marble">Vert Emeraude Marble</option>
                      <option value="Premium Granite">Premium Granite</option>
                      <option value="Premium Quartzite">Premium Quartzite</option>
                      <option value="Premium Travertine">Premium Travertine</option>
                      <option value="Multiple">Multiple types</option>
                      <option value="Other">Other / Not sure</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-stone-700 text-sm font-sans font-medium mb-2">
                      Required Quantity *
                    </label>
                    <input
                      type="text"
                      name="quantity"
                      required
                      className="w-full px-4 py-3 border border-stone-200 rounded-md text-sm font-sans text-stone-700 focus:outline-none focus:border-gold-500 transition-colors"
                      placeholder="e.g. 200 m² or 3 containers"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-stone-700 text-sm font-sans font-medium mb-2">
                      Budget Range (EUR)
                    </label>
                    <select
                      name="budget"
                      className="w-full px-4 py-3 border border-stone-200 rounded-md text-sm font-sans text-stone-700 focus:outline-none focus:border-gold-500 transition-colors bg-white"
                    >
                      <option value="">Select budget range</option>
                      <option value="Under 10k">Under €10,000</option>
                      <option value="10k-50k">€10,000 – €50,000</option>
                      <option value="50k-100k">€50,000 – €100,000</option>
                      <option value="100k-500k">€100,000 – €500,000</option>
                      <option value="Over 500k">Over €500,000</option>
                      <option value="Not sure">Not sure yet</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-stone-700 text-sm font-sans font-medium mb-2">
                      Application
                    </label>
                    <select
                      name="application"
                      className="w-full px-4 py-3 border border-stone-200 rounded-md text-sm font-sans text-stone-700 focus:outline-none focus:border-gold-500 transition-colors bg-white"
                    >
                      <option value="">Select application</option>
                      <option value="Kitchen">Kitchen countertops</option>
                      <option value="Bathroom">Bathroom / Spa</option>
                      <option value="Flooring">Flooring</option>
                      <option value="Wall Cladding">Wall cladding / Feature walls</option>
                      <option value="Hotel">Hotel / Hospitality</option>
                      <option value="Exterior">Exterior / Facade</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>

                {/* Project Details */}
                <div>
                  <label className="block text-stone-700 text-sm font-sans font-medium mb-2">
                    Project Details
                  </label>
                  <textarea
                    name="project_details"
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-stone-200 rounded-md text-sm font-sans text-stone-700 focus:outline-none focus:border-gold-500 transition-colors resize-none"
                    placeholder="Describe your project, timeline, delivery requirements, and any special requests..."
                  ></textarea>
                  <p className="text-stone-400 text-xs font-sans mt-1">Maximum 500 characters</p>
                </div>

                <button
                  type="submit"
                  disabled={formState === "submitting"}
                  className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-gold-500 text-white font-sans font-medium text-sm rounded-md hover:bg-gold-600 transition-colors disabled:opacity-60 cursor-pointer whitespace-nowrap"
                >
                  {formState === "submitting" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Submitting...
                    </>
                  ) : (
                    <>
                      Submit Request
                      <i className="ri-send-plane-line"></i>
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}